import { flow } from 'lodash';

import { TableListData } from './table-list-data';

import { TableListCell } from './table-list-cell';
import { TableListRow } from './table-list-row';
import { FieldDescriptor, TableListSchema } from './table-list-schema';
import { TableListValueType } from './table-list-value';
import { getValue } from './table-list-value-helpers';
import { hashCode } from '@mode/shared/util-js';

/**
 * This model aggregates the {@link TableListData} and
 * {@link TableListSchema} into {@link TableListRow}s for
 * the view layer
 */
export class TableListModel {
  private _schema: TableListSchema;
  private _data: TableListData[];

  constructor({ schema = { fields: [] }, data = [] }: { schema: TableListSchema; data: TableListData[] }) {
    this._schema = schema;
    this._data = data;
  }

  /**
   * Generate table body row data from input data
   */
  public bodyRows(): TableListRow[] {
    return this.buildTableRows(this._data);
  }

  /**
   * Generate table header row data from input data
   */
  public headerRow(): TableListRow {
    return this.buildHeaderRow(this._schema);
  }

  private buildHeaderRow(schema: TableListSchema): TableListRow {
    const headerData = this.buildHeaderData(schema);
    const dataType = TableListValueType.Text;

    return this.buildTableRow(headerData, dataType);
  }

  private buildHeaderData(schema: TableListSchema): TableListData {
    const fieldTitleMap = (field: FieldDescriptor) => ({ [field.name]: field.title });
    const combine = (a: any, b: any) => ({ ...a, ...b });

    return {
      values: schema.fields.map(fieldTitleMap).reduce(combine, {}),
    };
  }

  private buildTableRows(tableListData: TableListData[]): TableListRow[] {
    return tableListData.map((data) => this.buildTableRow(data));
  }

  private buildTableRow(tableListData: TableListData, valueType: TableListValueType | null = null): TableListRow {
    const toCell = flow([lookupValue, parseValue, buildCell]);
    const values: TableListCell[] = this._schema.fields.map(toCell);
    const hash: number = this.hashCells(values);
    const options: any = tableListData.options || null;

    function lookupValue(field: any) {
      let tooltip;

      if (typeof field.tooltip === 'function') {
        tooltip = field.tooltip(tableListData.values);
      }

      return [field, tableListData.values[field.name], valueType || field.type, tooltip];
    }

    function parseValue([field, valueData, type, tooltip]: [field: any, valueData: any, type: any, tooltip: any]) {
      return [field.name, getValue(valueData, type), tooltip];
    }

    function buildCell([field, value, tooltip]: [field: any, value: any, tooltip: any]) {
      return { field, value, tooltip };
    }

    return { hash, values, options };
  }

  private hashCells(fields: TableListCell[]): number {
    const rowData = fields.reduce((stringValues, field) => {
      const data = field.value.data;

      if (typeof data === 'string') {
        return stringValues + data;
      }

      return stringValues;
    }, '');

    return hashCode(rowData);
  }
}
